import type { InferGetServerSidePropsType, NextPage } from "next";
import Head from "next/head";
import NextImage from "next/image";
import { b } from "@/b";
import { HEADER_FRAGMENT } from "@/components/Header";
import { PageLayout } from "@/components/PageLayout";
import OUR_STORY_HERO from "@/img/our_story_hero.jpg";
import JOSH_IMG from "@/img/our_story_josh.jpg";
import { popupMiddleware } from "@/sanity/popup-middleware";
import { getApolloSSRProps, useHydratedQuery } from "@/util/apollo-client";

const OUR_STORY_QUERY = b.query("OurStory", (b) => [b.__fragment(HEADER_FRAGMENT)]);

const OurStory: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const { data } = useHydratedQuery(props);

  return (
    <>
      <Head>
        <title>{`Our Story | Visionary Meals`}</title>
        <meta
          name="description"
          content="From a family farm to a fast-growing movement, discover how Visionary Meals began—and why we're more than just a meal prep company."
        />
      </Head>
      <PageLayout data={data} noPadding>
        {/* Hero image */}
        <div className="relative w-full h-64 md:h-[28rem] lg:h-[32rem]">
          <NextImage
            src={OUR_STORY_HERO}
            alt="Visionary Meals team"
            fill
            className="object-cover object-[center_37%]"
            priority
          />
        </div>

        {/* Story content */}
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            {/* Text */}
            <div className="text-center lg:text-left">
              <h1 className="text-3xl lg:text-4xl font-bold text-brand-black">Our Story</h1>

              <p className="mt-6 text-sm lg:text-base text-gray-600 leading-relaxed">
                Visionary Meals was founded in 2018 off a family farm in Granville, Ohio, where
                owner, Josh, grew up. It started as a dream while attending Wittenberg University.
                Josh played 4 years of college football, winning 3 NCAC Conference Championships.
                Josh graduated with a Bachelor&apos;s degree in Sociology (Criminal Justice
                concentration) and a minor in Sports Management.
              </p>

              <p className="mt-6 text-sm lg:text-base text-gray-600 leading-relaxed">
                Meal prepping was always a lifestyle that Josh lived, but like so many others, he
                struggled with finding the time and patience that a task like meal prepping
                requires. It wasn&apos;t long into his college career where committing to this
                lifestyle became normalized. Due to his athletic career and love for fitness, he
                understood the importance of proper nutrition and became aware of the growing demand
                there was for meal prepping. From what started as a few ideas and dreams, turned
                into a reality very fast.
              </p>

              <p className="mt-6 text-sm lg:text-base text-gray-600 leading-relaxed">
                Today, Visionary Meals has had the opportunity to impact thousands of lives in a
                very short amount of time. Through its unique business model that utilizes pick up
                locations and locally-sourced ingredients, Visionary is on pace to lead the industry
                in meal prep.
              </p>

              <p className="mt-6 text-sm lg:text-base text-gray-600 leading-relaxed">
                But if you know anyone from Team Visionary or if you&apos;ve been a customer, you
                will learn fast that we are so much more than a meal prep company. Said best by
                Josh, &ldquo;We are not here to sell a product, but a feeling.&rdquo; Visionary has
                been built on love, compassion, empathy, and grace. Being a faith-based company, the
                mission behind the brand has always far surpassed the act of meal prepping. Every
                day, the mission of this company is to not just offer the best product on the
                market, but to leave a generational impact for our employees and a top-tier
                experience for our customers. Because of that, every step, milestone, and
                achievement has been built from purpose, fueled by passion, to be the best!
              </p>

              <p className="mt-6 text-sm lg:text-base text-gray-600 leading-relaxed">
                Visionary Meals is here because we understand the growing trend and desire to eat
                healthy, but we also understand that the fast-paced society we live in simply
                isn&apos;t slowing down. We go the extra mile to bring you locally-grown and
                farm-raised products to not only support our local farms, but to bring you the
                highest integrity product possible. Go check out the amazingly-curated dishes
                prepared by our chefs, because as a customer, you can count on that receiving your
                meals will be the highlight of your week. So come join our family, because your
                needs are our priority.
              </p>
            </div>

            {/* Photo */}
            <div className="relative aspect-[3/4] lg:aspect-auto rounded-2xl overflow-hidden mx-auto lg:mx-0 max-w-md lg:max-w-none w-full lg:sticky lg:top-24 lg:self-start lg:h-[600px]">
              <NextImage
                src={JOSH_IMG}
                alt="Josh, founder of Visionary Meals"
                fill
                className="object-cover object-center"
              />
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  );
};

export default OurStory;

export const getServerSideProps = popupMiddleware(async (ctx) => {
  return { props: await getApolloSSRProps(OUR_STORY_QUERY.document(), {}, ctx) };
});
